import { supabase } from './supabase'

/**
 * Invoke a Supabase edge function WITHOUT authentication
 * Use this for public endpoints like invite acceptance and welcome emails
 */
export async function invokeFunctionAnonymous<T = unknown>(
  functionName: string,
  payload: Record<string, unknown>
): Promise<{ data: T | null; error: unknown }> {
  console.log(`🔧 Starting anonymous edge function invocation: ${functionName}`)
  console.log('📦 Payload:', payload)

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

  console.log('🔑 Using anon key:', anonKey ? `${anonKey.substring(0, 20)}...` : 'MISSING')
  console.log('📡 Supabase URL:', supabaseUrl)

  // Use direct fetch instead of Supabase client to avoid body serialization issues
  const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`
  console.log('📡 Function URL:', functionUrl)

  try {
    // Stringify the payload
    const body = JSON.stringify(payload)
    console.log('📦 Stringified body:', body)
    console.log('📦 Body length:', body.length)

    // Add timeout to prevent hanging
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

    try {
      console.log('🚀 Making fetch request...')
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`
        },
        body: body,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      console.log('📡 Response status:', response.status)
      console.log('📡 Response ok:', response.ok)

      const responseText = await response.text()
      console.log('📡 Response text:', responseText)

      let data: T | null = null
      let error: unknown = null

      try {
        data = JSON.parse(responseText)
        console.log('✅ Parsed response data:', data)
      } catch (parseError) {
        console.error('❌ Failed to parse response:', parseError)
        error = { message: `Failed to parse response: ${responseText}` }
      }

      if (!response.ok) {
        error = data || { message: `HTTP ${response.status}: ${responseText}` }
        console.error('❌ Edge function error:', error)
        return { data: null, error }
      }

      return { data, error }
    } catch (err: unknown) {
      clearTimeout(timeoutId)
      if (err instanceof Error && err.name === 'AbortError') {
        console.error('⏰ Edge function timed out after 30 seconds')
        return { data: null, error: new Error('Request timed out. Please try again.') }
      }
      throw err
    }
  } catch (error: unknown) {
    console.error(`❌ Edge function error (${functionName}):`, error)
    if (error instanceof Error) {
      console.error('❌ Error name:', error.name)
      console.error('❌ Error message:', error.message)
      console.error('❌ Error stack:', error.stack)
    }
    return { data: null, error }
  }
}

/**
 * Invoke a Supabase edge function with proper error handling and auth
 */
export async function invokeFunction<T = unknown>(
  functionName: string,
  payload: Record<string, unknown>
): Promise<{ data: T | null; error: unknown }> {
  console.log(`🔧 Starting edge function invocation: ${functionName}`)
  console.log('Payload:', payload)
  
  try {
    // Get current session
    console.log('🔐 Getting session for edge function...')
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()
    
    if (sessionError) {
      console.error('🔐 Session error:', sessionError)
      return { data: null, error: sessionError }
    }
    
    if (!session) {
      console.error('🔐 No session available')
      return { data: null, error: new Error('No active session') }
    }
    
    console.log('🔐 Session found:', session.user?.email)
    console.log(`🔧 Invoking edge function: ${functionName}`)
    console.log('📡 Edge function URL:', `${(supabase as unknown as { functionsUrl: string }).functionsUrl}/${functionName}`)
    
    // Add timeout to prevent hanging
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    try {
      // Call the edge function with explicit headers
      const { data, error } = await supabase.functions.invoke<T>(functionName, {
        body: payload,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        // signal is not in the Supabase SDK types but is supported at runtime
        signal: controller.signal as unknown as undefined
      })
      
      clearTimeout(timeoutId)
      
      console.log('✅ Edge function response received')
      
      if (error) {
        console.error('❌ Edge function error:', error)
        // Try to parse error response for more details
        if (error.message) {
          console.error('❌ Error message:', error.message)
        }
        if (error && typeof error === 'object' && 'context' in error) {
          try {
            const errorBody = await (error as { context: { json: () => Promise<unknown> } }).context.json()
            console.error('❌ Error details:', errorBody)
          } catch (e) {
            // Could not parse error body
          }
        }
      }
      
      // Handle 401 errors specifically
      if (error && error.message?.includes('401')) {
        console.error('🔐 Edge function returned 401, trying to refresh session...')
        
        // Try to refresh the session
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession()
        
        if (refreshError || !newSession) {
          console.error('🔐 Session refresh failed:', refreshError)
          return { data: null, error: new Error('Authentication failed. Please log in again.') }
        }
        
        // Retry with new token
        console.log('🔐 Retrying with refreshed token...')
        const { data: retryData, error: retryError } = await supabase.functions.invoke<T>(functionName, {
          body: payload,
          headers: {
            Authorization: `Bearer ${newSession.access_token}`,
          }
        })
        
        return { data: retryData, error: retryError }
      }
      
      return { data, error }
    } catch (err: unknown) {
      clearTimeout(timeoutId)
      if (err instanceof Error && err.name === 'AbortError') {
        console.error('⏰ Edge function timed out after 30 seconds')
        return { data: null, error: new Error('Request timed out. Please try again.') }
      }
      throw err
    }
  } catch (error: unknown) {
    console.error(`Edge function error (${functionName}):`, error)
    return { data: null, error }
  }
}